import { useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { FileText, Upload, XCircle, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../components/ui/select";
import { useFileUpload } from "../../../hooks/useFileUpload";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const DPI_LEVELS = [600, 300, 150];
const ZOOM_LEVELS = [100, 200, 400];
const SCREEN_PPI = 96;
const PDF_PPI = 72;

const renderPageToDataUrl = async (
	doc: PDFDocumentProxy,
	pageNumber: number,
	scale: number
): Promise<string> => {
	const page = await doc.getPage(pageNumber);
	const viewport = page.getViewport({ scale });
	const canvas = document.createElement("canvas");
	const context = canvas.getContext("2d");
	if (!context) throw new Error("2D-Context nicht verfügbar");
	canvas.width = Math.ceil(viewport.width);
	canvas.height = Math.ceil(viewport.height);
	await page.render({ canvasContext: context, viewport }).promise;
	return canvas.toDataURL("image/png");
};

export const DpiSimulatorPage = () => {
	const docRef = useRef<PDFDocumentProxy | null>(null);
	const [fileName, setFileName] = useState<string | null>(null);
	const [numPages, setNumPages] = useState(0);
	const [pageNumber, setPageNumber] = useState(1);
	const [zoom, setZoom] = useState(200);
	const [pageWidthPt, setPageWidthPt] = useState(595);
	const [vectorUrl, setVectorUrl] = useState<string | null>(null);
	const [rasterUrls, setRasterUrls] = useState<Record<number, string>>({});
	const [isRendering, setIsRendering] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const renderVector = async (
		doc: PDFDocumentProxy,
		page: number,
		zoomValue: number
	) => {
		const scale = (zoomValue / 100) * (SCREEN_PPI / PDF_PPI);
		setVectorUrl(await renderPageToDataUrl(doc, page, scale));
	};

	const renderRasters = async (doc: PDFDocumentProxy, page: number) => {
		const urls: Record<number, string> = {};
		for (const dpi of DPI_LEVELS) {
			urls[dpi] = await renderPageToDataUrl(doc, page, dpi / PDF_PPI);
		}
		setRasterUrls(urls);
	};

	const renderAll = async (
		doc: PDFDocumentProxy,
		page: number,
		zoomValue: number
	) => {
		setIsRendering(true);
		setError(null);
		try {
			await renderVector(doc, page, zoomValue);
			await renderRasters(doc, page);
		} catch (e) {
			setError(
				e instanceof Error ? e.message : "Rendering fehlgeschlagen"
			);
		} finally {
			setIsRendering(false);
		}
	};

	const handleFile = async (file: File) => {
		setIsRendering(true);
		setError(null);
		try {
			const buffer = await file.arrayBuffer();
			const doc = await pdfjsLib.getDocument({ data: buffer }).promise;
			if (docRef.current) {
				docRef.current.destroy();
			}
			docRef.current = doc;
			const firstPage = await doc.getPage(1);
			setPageWidthPt(firstPage.getViewport({ scale: 1 }).width);
			setFileName(file.name);
			setNumPages(doc.numPages);
			setPageNumber(1);
			setVectorUrl(null);
			setRasterUrls({});
			await renderAll(doc, 1, zoom);
		} catch (e) {
			setError(
				e instanceof Error
					? e.message
					: "PDF konnte nicht geladen werden"
			);
			setIsRendering(false);
		}
	};

	const handlePageChange = (value: string) => {
		const page = parseInt(value);
		setPageNumber(page);
		if (docRef.current) {
			renderAll(docRef.current, page, zoom);
		}
	};

	const handleZoomChange = async (value: string) => {
		const zoomValue = parseInt(value);
		setZoom(zoomValue);
		if (!docRef.current) return;
		setIsRendering(true);
		try {
			await renderVector(docRef.current, pageNumber, zoomValue);
		} catch (e) {
			setError(
				e instanceof Error ? e.message : "Rendering fehlgeschlagen"
			);
		} finally {
			setIsRendering(false);
		}
	};

	const { isDragging, dragHandlers } = useFileUpload({
		onFileUpload: (files) => {
			if (files.length > 0) {
				handleFile(files[0]);
			}
		},
	});

	const cssWidth = pageWidthPt * (SCREEN_PPI / PDF_PPI) * (zoom / 100);

	const columns = [
		{ label: "Vektor (Referenz)", url: vectorUrl, pixelated: false },
		...DPI_LEVELS.map((dpi) => ({
			label: `${dpi} dpi`,
			url: rasterUrls[dpi] ?? null,
			pixelated: true,
		})),
	];

	return (
		<div className="flex flex-col px-12 py-6">
			<HeaderPortal>
				<PageTitle
					title={[
						{ name: "Support", link: "/support" },
						{ name: "DPI-Simulation" },
					]}
					icon={
						<FileText
							width="16px"
							height="16px"
							color="rgb(55, 65, 81)"
						/>
					}
				/>
			</HeaderPortal>

			<div className="flex flex-col gap-6 py-4">
				<div className="flex flex-col gap-2">
					<h1 className="text-2xl font-bold text-gray-900">
						DPI-Simulation
					</h1>
					<p className="text-gray-600">
						Simuliert, wie ein PDF beim Rasterdruck
						(Treiber-Kompatibilitätsmodus) auf verschiedenen
						DPI-Stufen aussieht — im Vergleich zur Vektor-Referenz.
						Die Raster-Varianten werden pixelgenau hochskaliert, um
						die Druckpunkte sichtbar zu machen.
					</p>
				</div>

				<div
					className={`bg-white border-2 border-dashed rounded-lg p-6 transition-all ${
						isDragging
							? "border-blue-500 bg-blue-50"
							: "border-gray-300 hover:border-blue-400"
					}`}
					{...dragHandlers}
				>
					<div className="flex items-center justify-center gap-4">
						<Upload
							className={`w-8 h-8 ${
								isDragging ? "text-blue-500" : "text-gray-400"
							}`}
						/>
						<p className="text-sm text-gray-600">
							{isDragging
								? "Datei hier ablegen..."
								: fileName
									? `Geladen: ${fileName}`
									: "PDF hierher ziehen oder auswählen"}
						</p>
						<label>
							<input
								type="file"
								accept=".pdf,application/pdf"
								onChange={(e) => {
									const file = e.target.files?.[0];
									if (file) {
										handleFile(file);
									}
									e.target.value = "";
								}}
								className="hidden"
								aria-label="PDF-Datei auswählen"
								data-testid="dpi-simulator-file-input"
							/>
							<Button
								type="button"
								variant="outline"
								size="sm"
								aria-label="PDF-Datei auswählen"
								data-testid="dpi-simulator-file-button"
								onClick={(e) => {
									e.preventDefault();
									(
										e.currentTarget
											.previousElementSibling as HTMLInputElement
									)?.click();
								}}
							>
								Datei auswählen
							</Button>
						</label>
					</div>
				</div>

				{error && (
					<div className="flex items-center gap-2 text-red-600 text-sm">
						<XCircle className="w-4 h-4 shrink-0" />
						{error}
					</div>
				)}

				{fileName && (
					<div className="flex items-center gap-4">
						<div className="flex items-center gap-2">
							<span className="text-sm text-gray-500">Seite</span>
							<Select
								value={String(pageNumber)}
								onValueChange={handlePageChange}
								disabled={isRendering}
							>
								<SelectTrigger
									className="h-8 w-20 text-sm"
									data-testid="dpi-simulator-page-select"
									aria-label="Seite wählen"
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{Array.from(
										{ length: numPages },
										(_, i) => (
											<SelectItem
												key={i + 1}
												value={String(i + 1)}
											>
												{i + 1}
											</SelectItem>
										)
									)}
								</SelectContent>
							</Select>
							<span className="text-sm text-gray-500">
								von {numPages}
							</span>
						</div>
						<div className="flex items-center gap-2">
							<span className="text-sm text-gray-500">Zoom</span>
							<Select
								value={String(zoom)}
								onValueChange={handleZoomChange}
								disabled={isRendering}
							>
								<SelectTrigger
									className="h-8 w-24 text-sm"
									data-testid="dpi-simulator-zoom-select"
									aria-label="Zoom wählen"
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{ZOOM_LEVELS.map((z) => (
										<SelectItem key={z} value={String(z)}>
											{z}%
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						{isRendering && (
							<div className="flex items-center gap-2 text-sm text-gray-500">
								<Loader2 className="w-4 h-4 animate-spin" />
								Wird gerendert...
							</div>
						)}
					</div>
				)}

				{fileName && (
					<div className="overflow-auto border border-gray-200 rounded-lg bg-gray-100 max-h-[75vh]">
						<div className="flex gap-6 p-4 w-max">
							{columns.map((column) => (
								<div
									key={column.label}
									style={{ width: cssWidth }}
									className="shrink-0"
								>
									<div className="sticky top-0 z-10 bg-gray-100 py-1 mb-1">
										<span className="text-sm font-semibold text-gray-700">
											{column.label}
										</span>
									</div>
									{column.url ? (
										<img
											src={column.url}
											alt={`Seite ${pageNumber} — ${column.label}`}
											style={{ width: cssWidth }}
											className={`border border-gray-300 bg-white ${
												column.pixelated
													? "[image-rendering:pixelated]"
													: ""
											}`}
											data-testid={`dpi-simulator-panel-${column.label.replace(
												/\s.*/,
												""
											)}`}
										/>
									) : (
										<div
											style={{
												width: cssWidth,
												height: cssWidth * 1.414,
											}}
											className="flex items-center justify-center border border-gray-300 bg-white text-gray-400"
										>
											<Loader2 className="w-6 h-6 animate-spin" />
										</div>
									)}
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
